import { Eye, EyeOff } from 'lucide-react';
import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface TextFieldProps extends Omit<React.ComponentProps<'input'>, 'type' | 'className'> {
  label?: string;
  type?: 'text' | 'email' | 'password';
  hint?: string;
  /** 검증 에러 메시지. 공백(" ")이면 테두리만 강조하고 메시지는 두지 않는다. */
  error?: string;
  className?: string;
}

/**
 * 텍스트 입력 필드 — Admin Kit Field(라벨·설명·오류) + Input. password 는 표시 토글.
 * react-hook-form 과는 `{...register('email')}` 스프레드로 연결한다(ref 전달 지원).
 */
export function TextField({
  label,
  type = 'text',
  hint,
  error = '',
  required,
  className,
  id,
  ...inputProps
}: TextFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;
  const hasError = error.length > 0;
  const errorMessage = error.trim();

  return (
    <Field data-invalid={hasError || undefined} className={cn('gap-2', className)}>
      {label && (
        <FieldLabel htmlFor={fieldId}>
          {label}
          {required && <span className="text-destructive">*</span>}
        </FieldLabel>
      )}
      <div className="relative">
        <Input
          id={fieldId}
          type={inputType}
          required={required}
          aria-invalid={hasError || undefined}
          className={cn(isPassword && 'pr-10', hasError && 'border-destructive')}
          {...inputProps}
        />
        {isPassword && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-1/2 right-1.5 -translate-y-1/2 text-muted-foreground"
            aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
            aria-pressed={showPassword}
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? <EyeOff /> : <Eye />}
          </Button>
        )}
      </div>
      {errorMessage ? (
        <FieldError>{errorMessage}</FieldError>
      ) : hint ? (
        <FieldDescription>{hint}</FieldDescription>
      ) : null}
    </Field>
  );
}
